import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Tabs, Tab } from '@mui/material';
import StationeryEnquiriesTab from './StationeryEnquiriesTab';
import StationeryOrdersTabContent from './StationeryOrdersTabContent';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stationery-tabpanel-${index}`}
      aria-labelledby={`stationery-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `stationery-tab-${index}`,
    'aria-controls': `stationery-tabpanel-${index}`,
  };
}

function StationeryTabs({ 
  stationeryOrders,
  stationeryEnquiries = [],
  loading,
  error,
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  statusFilter,
  setStatusFilter,
  filterOrders,
  sortOrders,
  getProcessedOrders,
  getUniqueStatuses,
  getTotalPrice,
  handleViewDetails,
  handleStatusChange,
  ActionsMenu,
  handleViewEnquiry,
  handleConvertEnquiryToOrder,
  handleCreateOrganization
}) {
  // Try to retrieve saved tab from localStorage, default to tab 0 (orders)
  const savedTab = localStorage.getItem('stationeryActiveTab');
  const [activeTab, setActiveTab] = useState(savedTab ? parseInt(savedTab) : 0);

  // When activeTab changes, save it to localStorage
  useEffect(() => {
    localStorage.setItem('stationeryActiveTab', activeTab.toString());
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="stationery tabs"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`Orders (${stationeryOrders.length})`} {...a11yProps(0)} />
          <Tab label={`Enquiries (${stationeryEnquiries.length})`} {...a11yProps(1)} />
        </Tabs>
      </Box>
      
      <TabPanel value={activeTab} index={0}>
        <StationeryOrdersTabContent 
          stationeryOrders={stationeryOrders}
          loading={loading}
          error={error}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          filterOrders={filterOrders}
          sortOrders={sortOrders}
          getProcessedOrders={getProcessedOrders}
          getUniqueStatuses={getUniqueStatuses}
          getTotalPrice={getTotalPrice}
          handleViewDetails={handleViewDetails}
          handleStatusChange={handleStatusChange}
          ActionsMenu={ActionsMenu}
        />
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        <StationeryEnquiriesTab 
          enquiries={stationeryEnquiries}
          loading={loading}
          error={error}
          onViewEnquiry={handleViewEnquiry}
          onConvertToOrder={handleConvertEnquiryToOrder}
          onCreateOrganization={handleCreateOrganization}
        />
      </TabPanel>
    </Box>
  );
}

StationeryTabs.propTypes = {
  stationeryOrders: PropTypes.array.isRequired,
  stationeryEnquiries: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  setSortBy: PropTypes.func.isRequired,
  sortOrder: PropTypes.string.isRequired,
  setSortOrder: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  filterOrders: PropTypes.func.isRequired,
  sortOrders: PropTypes.func.isRequired,
  getProcessedOrders: PropTypes.func.isRequired,
  getUniqueStatuses: PropTypes.func.isRequired,
  getTotalPrice: PropTypes.func.isRequired,
  handleViewDetails: PropTypes.func.isRequired,
  handleStatusChange: PropTypes.func.isRequired,
  ActionsMenu: PropTypes.elementType.isRequired,
  handleViewEnquiry: PropTypes.func,
  handleConvertEnquiryToOrder: PropTypes.func,
  handleCreateOrganization: PropTypes.func
};

export default StationeryTabs;
